export interface SerializableErrorData {
  message: string
  type: string
  code?: string
  stack?: string
  // oxlint-disable-next-line typescript/no-explicit-any -- any data
  details?: any // eg validation errors

  cause?: SerializableErrorData
  errors?: SerializableErrorData[] // Present for AggregateError
  error?: SerializableErrorData // Present for SuppressedError: newer failure
  suppressed?: SerializableErrorData // Present for SuppressedError: earlier failure
}

class SerializedError {
  data: SerializableErrorData
  constructor(data: SerializableErrorData) {
    this.data = data
  }
}

// server responds with this
export interface ServerErrorData extends SerializableErrorData {
  status: number // our backend service status code (eg our is 500 when 3rd party is 4xx)
}

export class ServerError extends Error {
  status: number
  details?: unknown

  constructor(
    message: string,
    status: number,
    options?: { cause?: Error; details?: unknown },
  ) {
    super(message, { cause: options?.cause })
    this.status = status
    this.details = options?.details
  }
}

export interface ClientErrorData extends SerializableErrorData {
  componentStack?: string
}

interface RequestErrorData extends SerializableErrorData {
  // message should contain url and method info
  statusCode: number // 3rd party backend response status code
}

// this class will be thrown on client with SerializedError as cause
export class RequestError extends Error {
  statusCode: number
  details?: unknown
  /** Retry-After value in ms (parsed from header). Hints retry delay for 429s. */
  retryAfterMs?: number

  constructor(
    message: string,
    status: number,
    options?: {
      cause?: Error | SerializedError
      details?: unknown
      retryAfterMs?: number
    },
  ) {
    super(message, { cause: options?.cause })
    this.statusCode = status
    this.details = options?.details
    this.retryAfterMs = options?.retryAfterMs
  }
}

interface DomainErrorData extends SerializableErrorData {
  // oxlint-disable-next-line typescript/no-explicit-any -- any data
  userMessage?: any[] // data for user message translation function
  shouldContactSupport?: boolean
}

export class DomainError<TErrorType extends string = string> extends Error {
  type: TErrorType
  // oxlint-disable-next-line typescript/no-explicit-any -- any data
  userMessage: any[]
  shouldContactSupport?: boolean

  constructor(
    type: TErrorType,
    message: string,
    // oxlint-disable-next-line typescript/no-explicit-any -- aaa
    userMessage: any[],
    opts?: { shouldContactSupport?: boolean; cause?: Error },
  ) {
    super(message, { cause: opts?.cause })
    this.type = type
    this.shouldContactSupport = opts?.shouldContactSupport
    this.userMessage = userMessage
  }
}

export function makeDomainError<TErrorType extends string>() {
  return DomainError<TErrorType>
}

export function unknownErrorToError(error: unknown): Error {
  if (error instanceof Error) return error
  return new Error(String(error))
}

export function serializeError(error: unknown): SerializableErrorData {
  const seen = new WeakSet<Error>()

  function serialize(error: unknown): SerializableErrorData {
    if (error instanceof SerializedError) return error.data

    const obj = unknownErrorToError(error)

    if (seen.has(obj)) {
      return {
        message: '[Circular]',
        type: 'CircularReference',
      }
    }

    seen.add(obj)

    if (typeof AggregateError !== 'undefined' && obj instanceof AggregateError) {
      const result: SerializableErrorData = {
        message: obj.message,
        type: obj.name,
        ...serializeErrorMetadata(obj),
        stack: obj.stack,
        errors: obj.errors.map((error: unknown) => serialize(error)),
      }
      if ('cause' in obj) result.cause = serialize(obj.cause)
      return result
    }

    if (typeof SuppressedError !== 'undefined' && obj instanceof SuppressedError) {
      const result: SerializableErrorData = {
        message: obj.message,
        type: obj.name,
        ...serializeErrorMetadata(obj),
        stack: obj.stack,
        error: serialize(obj.error),
        suppressed: serialize(obj.suppressed),
      }
      if ('cause' in obj) result.cause = serialize(obj.cause)
      return result
    }

    if (obj instanceof ServerError) {
      const result: ServerErrorData = {
        message: obj.message,
        type: 'ServerError',
        ...serializeErrorMetadata(obj),
        stack: obj.stack,
        details: obj.details,
        status: obj.status,
      }
      if (obj.cause) result.cause = serialize(obj.cause)
      return result
    }

    if (obj instanceof RequestError) {
      const result: RequestErrorData = {
        message: obj.message,
        type: 'RequestError',
        ...serializeErrorMetadata(obj),
        stack: obj.stack,
        details: obj.details,
        statusCode: obj.statusCode,
      }
      if (obj.cause) result.cause = serialize(obj.cause)
      return result
    }

    if (obj instanceof DomainError) {
      const result: DomainErrorData = {
        message: obj.message,
        // oxlint-disable-next-line typescript/no-unsafe-assignment -- DomainError generic type param
        type: obj.type,
        ...serializeErrorMetadata(obj),
        stack: obj.stack,
        userMessage: obj.userMessage,
        shouldContactSupport: obj.shouldContactSupport,
      }
      if (obj.cause) result.cause = serialize(obj.cause)
      return result
    }

    if ('componentStack' in obj) {
      const result: ClientErrorData = {
        message: obj.message,
        type: obj.name,
        ...serializeErrorMetadata(obj),
        stack: obj.stack,
        componentStack: (obj as { componentStack: string }).componentStack,
      }
      if (obj.cause) result.cause = serialize(obj.cause)
      return result
    }

    const result: SerializableErrorData = {
      message: obj.message,
      type: obj.name,
      ...serializeErrorMetadata(obj),
      stack: obj.stack,
    }

    if (obj.cause) result.cause = serialize(obj.cause)
    return result
  }

  return serialize(error)
}

function serializeErrorMetadata(error: Error): Pick<ClientErrorData, 'code' | 'details' | 'componentStack'> {
  const { code, details, componentStack } = error as Error & {
    code?: unknown
    details?: unknown
    componentStack?: unknown
  }
  return {
    ...(typeof code === 'string' ? { code } : {}),
    ...(details === undefined ? {} : { details }),
    ...(typeof componentStack === 'string' ? { componentStack } : {}),
  }
}

export function extractDomainError(error: unknown): {
  isDomainError: boolean
  domainErrorData?: DomainErrorData
} {
  const seen = new WeakSet<object>()

  function checkError(error: unknown): DomainErrorData | null {
    if (!error) return null

    // Check if it's a DomainError instance
    if (error instanceof DomainError) {
      const result: DomainErrorData = {
        message: error.message,
        // oxlint-disable-next-line typescript/no-unsafe-assignment -- DomainError generic type param
        type: error.type,
        stack: error.stack,
        userMessage: error.userMessage,
        shouldContactSupport: error.shouldContactSupport,
      }
      if (error.cause) result.cause = checkError(error.cause) ?? undefined
      return result
    }

    // Check if it's a SerializedError wrapper with DomainErrorData
    if (error instanceof SerializedError) {
      const data = error.data
      if ('userMessage' in data) {
        return data as unknown as DomainErrorData
      }
      // Continue checking the cause of serialized error
      if (data.cause) return checkError(data.cause)
      return null
    }

    // Check if it's a plain object with serialized error data
    if (typeof error === 'object' && error !== null && !seen.has(error)) {
      seen.add(error)

      const obj = error as Record<string, unknown>

      // Check if it's serialized DomainErrorData
      if ('userMessage' in obj) {
        return obj as unknown as DomainErrorData
      }

      // Check if it's an Error with a cause property
      if ('cause' in obj && obj.cause) {
        return checkError(obj.cause)
      }
    }

    return null
  }

  const domainErrorData = checkError(error)
  return {
    isDomainError: domainErrorData !== null,
    domainErrorData: domainErrorData ?? undefined,
  }
}

let errorsMapper = (error: unknown) => error // server should respond with domain errors as first cause. this mapper is for errors from 3rd parties
export const mapError = (error: unknown) => errorsMapper(error)
export function setErrorsMapper(mapper: (error: unknown) => unknown): void {
  errorsMapper = mapper
}
